import useSWR from "swr"
import { SettingsLayout } from "./layout"
import { LedgerAccountUploadSection } from "./ledger_account_upload"
import { LedgerAccountManageSection } from "./ledger_account_manage"
import { ReasonCodeMappingSection } from "./reason_code_mapping"
import { QuickBooksDesktopSettings } from "./quickbooks_desktop"
import { XeroCreditMemoLineModeSettings } from "./xero_credit_memo_line_mode"
import { AccountingSkillsFileEditor } from "./accounting_skills_file"
import { AccountingAutoCodeSettingsSection } from "./accounting_auto_code_settings"
import { AccountingSyncBanner } from "./accounting_sync_banner"
import { AccountingSyncExplainer } from "@/accounting/accounting_explainers"
import { AccountingSyncedDataSection } from "./accounting_synced_data"
import { usePageTitle } from "@/utils"
import { Button } from "@/components/ui/button"
import { Link } from "wouter-preact"
import { findAccountingIntegration, getIntegrationsWithJobs } from "@/api/integration"
import { useEffect, useState } from "preact/hooks"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { toast } from "@/components/ui/use-toast"
import { use_coding_wiz_preferences } from "@/api/coding_wiz_preferences"
import { coding_wiz_split_grouping_valid } from "@/accounting/coding_wiz_preferences"
import {
  prefs_from_split_grouping_value,
  SplitGroupingOptions,
  split_grouping_value_from_prefs,
  type SplitGroupingValue,
} from "@/accounting/split_components/split_grouping_options"
import { MonthClosesSettings } from "./month_closes"
import { OpenDeductionsSettings } from "./integrations/open-deductions-settings"
import { SageLocationSettings } from "./integrations/sage-location-settings"
import { SageSyncSettings } from "./integrations/sage-sync-settings"
import { NetsuiteClearingAccountSettings, NetsuiteInterCustomerClearingSettings } from "./integrations/netsuite-clearing-account-settings"
import { NetsuiteLocationSettings } from "./integrations/netsuite-location-settings"
import { NetsuiteEarlyPaySettings } from "./integrations/netsuite-early-pay-settings"

function AccountingIntegrationSyncSection() {
  const { data: integrations_result, mutate: mutate_integrations } = useSWR(
    "integrations-with-jobs",
    () => getIntegrationsWithJobs()
  )

  const accounting_integration = integrations_result?.ok
    ? findAccountingIntegration(integrations_result.value.data)
    : undefined

  if (!accounting_integration) {
    return null
  }

  return (
    <>
      <AccountingSyncBanner
        integration={accounting_integration}
        onSynced={() => {
          void mutate_integrations()
        }}
      />
      <AccountingSyncExplainer system={accounting_system_label(accounting_integration.name)} className="mt-4" />
    </>
  )
}

/**
 * The provider-specific accounting settings (holding account, clearing accounts, early-pay,
 * credit memo location, sync window), which used to live only on `/settings/config/<provider>`,
 * reachable through the Connections card. Rendered here so one page holds every accounting setting.
 */
function AccountingProviderSettingsSections() {
  const { data: integrations_result } = useSWR("integrations-with-jobs", () => getIntegrationsWithJobs())
  const integration = integrations_result?.ok ? findAccountingIntegration(integrations_result.value.data) : undefined
  if (!integration || (integration.name !== "netsuite" && integration.name !== "sageintacct")) {
    return null
  }
  const provider = integration.name
  return (
    <>
      <div class="border rounded-lg p-6 mt-8 space-y-4">
        <h2 class="text-lg font-semibold">Holding account and month end</h2>
        <p class="text-sm text-gray-600">
          With a holding account set, applying a check posts the cash today and parks deductions there; they are cleared
          to their GL accounts from Accounting › Open deductions once coded and reviewed.
        </p>
        <OpenDeductionsSettings integration={integration} provider={provider} />
        {provider === "netsuite" && <NetsuiteClearingAccountSettings integration={integration} />}
        {provider === "netsuite" && <NetsuiteInterCustomerClearingSettings integration={integration} />}
      </div>
      {provider === "netsuite" && (
        <div class="border rounded-lg p-6 mt-8 space-y-4">
          <h2 class="text-lg font-semibold">Early-pay discounts</h2>
          <NetsuiteEarlyPaySettings integration={integration} />
        </div>
      )}
      <div class="border rounded-lg p-6 mt-8 space-y-4">
        <h2 class="text-lg font-semibold">Credit memo location</h2>
        {provider === "netsuite" ? (
          <NetsuiteLocationSettings integration={integration} />
        ) : (
          <SageLocationSettings integration={integration} />
        )}
      </div>
      {provider === "sageintacct" && (
        <div class="border rounded-lg p-6 mt-8 space-y-4">
          <h2 class="text-lg font-semibold">Sync window</h2>
          <SageSyncSettings integration={integration} />
        </div>
      )}
    </>
  )
}

/** The team's CodingWiz defaults: one line or a split, and what to split by. Bulk runs use these. */
function CodingWizDefaultsSection() {
  const { prefs, saved, loading, save } = use_coding_wiz_preferences()
  const [split_into_multiple_lines, set_split_into_multiple_lines] = useState(prefs.split_into_multiple_lines)
  const [grouping, set_grouping] = useState<SplitGroupingValue>(split_grouping_value_from_prefs(prefs))
  const [saving, set_saving] = useState(false)
  useEffect(() => {
    set_split_into_multiple_lines(prefs.split_into_multiple_lines)
    set_grouping(split_grouping_value_from_prefs(prefs))
  }, [prefs])
  const draft = prefs_from_split_grouping_value(split_into_multiple_lines, grouping)
  const valid = coding_wiz_split_grouping_valid(draft)

  async function handle_save() {
    set_saving(true)
    const ok = await save(draft)
    set_saving(false)
    toast(ok ? { title: "Saved", description: "CodingWiz defaults updated" } : { title: "Could not save", variant: "destructive" })
  }

  return (
    <div class="border rounded-lg p-6 mt-8 space-y-4">
      <h2 class="text-lg font-semibold">CodingWiz defaults</h2>
      <p class="text-sm text-gray-600">
        How CodingWiz codes when it runs on many rows at once (the check page's "CodingWiz on the rest" step and the
        selection bar). A single run can still override these.{" "}
        {!loading && !saved && "Nothing is saved yet, so the built-in default applies: one ledger code per deduction."}
      </p>
      <div class="flex items-start gap-3">
        <Checkbox
          id="coding-wiz-default-split"
          checked={split_into_multiple_lines}
          onCheckedChange={(checked: boolean | "indeterminate") => set_split_into_multiple_lines(checked === true)}
        />
        <Label htmlFor="coding-wiz-default-split" class="cursor-pointer">
          Split into multiple lines (otherwise one ledger code per deduction)
        </Label>
      </div>
      {split_into_multiple_lines && <SplitGroupingOptions value={grouping} onChange={set_grouping} />}
      {!valid && <p class="text-xs text-red-600">Select at least one grouping option for multi-line splits.</p>}
      <Button onClick={() => void handle_save()} disabled={saving || loading || !valid}>
        {saving ? "Saving…" : "Save defaults"}
      </Button>
    </div>
  )
}

/** Display name of an accounting integration, as the explainers and the Apply page spell it. */
export function accounting_system_label(name: string): string {
  switch (name) {
    case "xero":
      return "Xero"
    case "netsuite":
      return "NetSuite"
    case "sage_intacct":
      return "Sage Intacct"
    default:
      return "QuickBooks"
  }
}

function AccountingSyncedDataSectionGate() {
  const { data: integrations_result } = useSWR("integrations-with-jobs", () => getIntegrationsWithJobs())
  const accounting_integration = integrations_result?.ok
    ? findAccountingIntegration(integrations_result.value.data)
    : undefined

  if (!accounting_integration) {
    return null
  }

  return <AccountingSyncedDataSection />
}

/** The credit-memo line grouping toggle only applies to the Xero write-back. */
function XeroCreditMemoLineModeGate() {
  const { data: integrations_result } = useSWR("integrations-with-jobs", () => getIntegrationsWithJobs())
  const accounting_integration = integrations_result?.ok
    ? findAccountingIntegration(integrations_result.value.data)
    : undefined

  if (accounting_integration?.name !== "xero") {
    return null
  }

  return <XeroCreditMemoLineModeSettings />
}

export function SettingsAccounting() {
  usePageTitle("Accounting")
  return (
    <SettingsLayout
      action={
        <Link href="/settings/connect#accounting">
          <Button variant="outline" class="text-sm">
            Add Connection
          </Button>
        </Link>
      }>
      <AccountingIntegrationSyncSection />
      <AccountingSyncedDataSectionGate />
      <LedgerAccountManageSection />
      <LedgerAccountUploadSection />
      <ReasonCodeMappingSection />
      <AccountingAutoCodeSettingsSection />
      <CodingWizDefaultsSection />
      <div class="border rounded-lg p-6 mt-8 space-y-2">
        <h2 class="text-lg font-semibold">CodingWiz knowledge base</h2>
        <AccountingSkillsFileEditor />
      </div>
      <AccountingProviderSettingsSections />
      <div class="border rounded-lg p-6 mt-8 space-y-2">
        <h2 class="text-lg font-semibold">Month close dates</h2>
        <p class="text-sm text-gray-600">
          Lock dates for the cash application preview: a line dated inside a closed month gets a posting date in the first
          open one. Xero connections read these from Xero instead.
        </p>
        <MonthClosesSettings />
      </div>
      <XeroCreditMemoLineModeGate />
      <QuickBooksDesktopSettings />
    </SettingsLayout>
  )
}
